import React, { Fragment, useEffect, useRef, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, Dialog, Grid, LinearProgress, linearProgressClasses, Stack, Typography, Button, IconButton, TextField, DialogContent } from "@mui/material";
import { Check, FiberManualRecord } from "@mui/icons-material";
import Tutor from "./Tutor";
import Events from "./Events";
import SessionHistory from "./SessionHistory";
import ProjectMemberList from "./ProjectMemberList";
import CommonCard from "../../common/components/CommonCard";
import CommonDialog from "../../common/components/CommonDialog";
import CommonButton from "../../common/components/CommonButton";
import theme from "../../theme";
import Details from "./Detail";
import MileStone from "./MileStone";
import WorkLogs from "./WorkLogs";
import Reflection from "./Reflection";
import styled from "@emotion/styled";
import CurrentPlan from "../../common/components/CurrentPlan";
import ProjectSetting from "./ProjectSetting";
import { ExamTypes, ServiceOffer, UserTypeId } from "../../common/const";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { getProject, getprojectProgress } from "../../api/projectApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { deleteProjectResearch } from "../../api/resourceApi";
import { getMileStone } from "../../api/mileStoneApi";
import Tasks from "../Dcp Tutoring/Tasks";
import { getStudentData, getUser } from "../../api/authApi";
import { confirmBooking, getEventByInvite } from "../../api/sessionApi";
import { Close, WatchLater, } from "@mui/icons-material";
import moment from "moment";

const ExecutionType = {
    Details: 'Project Details',
    MileStone: 'Milestones & Status',
    Task: 'Tasks & Work Logs',
    Reflection: 'Reflection & Learning'
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: 5,
    marginTop: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[200],
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: '#10AE2D',
        ...theme.applyStyles('dark', {
            backgroundColor: '#10AE2D',
        }),
    },
}));

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});


const ProjectDetails = ({
    fromMentor
}) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, user } = useSelector((state) => state.auth);
    const searchParams = new URLSearchParams(location.search);
    const detailsRef = useRef(null);
    const milestoneRef = useRef(null);
    const taskRef = useRef(null);
    const reflectionRef = useRef(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [executionType, setExecutionType] = useState(ExecutionType.Details);
    const [progress, setProgress] = useState(null);
    const [mileStones, setMileStones] = useState([]);
    const [projectData, setProjectData] = useState(null);
    const [studentData, setStudentData] = useState(null);
    const [coachData, setCoachData] = useState(null);
    const [initialData, setInitialData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);
    const [sessionNote, setSessionNote] = useState('');


    const scrollToSection = (ref) => {
        ref.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'  // 'start', 'center', 'end', or 'nearest'
        });
    };

    const getProjectDetails = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getProject(searchParams.get('id'));
            const progressRes = await getprojectProgress(searchParams.get('id'));
            setProgress(progressRes?.data?.progress);
            setProjectData(response?.data);
            const studentRes = await getStudentData(response?.data?.student_id);
            if (studentRes?.data?.user_id) {
                const userResponse = await getUser(studentRes?.data?.user_id);
                setStudentData(userResponse?.data);
                dispatch(setLoading({
                    loading: false
                }));
            }
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (searchParams.get('id')) {
            getProjectDetails();
        }
    }, [searchParams.get('id')]);

    const getMileStones = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getMileStone(projectData?.project_id);
            setMileStones(response?.data);
            dispatch(setLoading({
                loading: false
            }))
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (projectData?.project_id) {
            getMileStones();
        }
    }, [projectData]);

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookSessionDialog(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (initialData !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [initialData]);

    const handleCloseAll = () => {
        setBookSessionDialog(false);
        setSessionNote('');
        setEventData(null);
    };

    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                "mentor_profile_id": projectData?.mentor_profile_id,
                "student_id": user?.student_id,
                "service_offers": [ServiceOffer.mentor],
                "notes": sessionNote,
                "status": "CONFIRMED",
                "invitee_email": eventData.invitee_email,
                "calendly_event_uri": eventData.calendly_event_uri,
                "calendly_invitee_uri": eventData.calendly_invitee_uri,
                "project_id": projectData?.project_id
            }
            const response = await confirmBooking(data);
            if (response) {
                dispatch(setLoading({
                    loading: false
                }));
                setBookSessionDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialData(null);
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };


    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseAll}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: '#6B7280',
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                        Confirm Your Session
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <img
                                    src='/Images/graycalendar.png'
                                    style={{
                                        width: 20,
                                        position: 'relative'
                                    }}
                                    alt=''
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Date
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: '#F9FAFB',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                        Time
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                            Any specific question for this session
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Write here"
                            value={sessionNote}
                            onChange={(e) => setSessionNote(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        fullWidth
                        onClick={handleConfirmBooking}
                        sx={{
                            backgroundColor: '#1E3A8A',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: '8px',
                            height: '48px',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#1E40AF',
                            },
                        }}
                    >
                        Confirm Booking
                    </Button>
                </DialogContent>
            </StyledDialog>
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete project?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await deleteProjectResearch(projectData?.project_id);
                                    if (response?.success) {
                                        showNotification(response?.message, 'success');
                                        setDeleteOpen(false);
                                        navigate('/build-project');
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <PageHeader
                title={fromMentor ? "Project Workspace" : "STEM Project Details"}
                content={
                    !fromMentor && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                                My DCP Services
                            </Typography>
                            <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                            <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                                Project Details
                            </Typography>
                        </Stack>
                    )
                }
            />
            <Grid container spacing={2} mt={0.5}>
                <Grid item md={8}>
                    <Box display="flex" gap={1} alignItems="center">
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            background: '#10AE2D',
                            width: 70,
                            height: 70,
                            position: 'relative'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                border: '2px solid white',
                                width: 30,
                                height: 30
                            }}>
                                <Check sx={{ color: 'white', fontSize: 14 }} />
                            </Box>
                        </Box>
                        <Box width="90%">
                            <Typography color="#10AE2D" fontSize={16} fontWeight={600}>Your Project Is {progress}% Completed</Typography>
                            <Typography color="background.dark" fontSize={13}>{progress}% of your project is completed. The remaining steps will shape the final experience.</Typography>
                            <BorderLinearProgress variant="determinate" value={progress} />
                        </Box>
                    </Box>
                    <Tutor projectData={projectData} studentId={searchParams?.get('sid')} coachData={coachData} setCoachData={setCoachData} setInitialData={setInitialData} />
                    <CommonCard
                        borderRadius={2}
                        paddingBottom="0px"
                        content={
                            <Fragment>
                                <Box>
                                    <Typography fontWeight={600}>Project Execution</Typography>
                                    <Typography fontSize={14} color={theme.palette.background.dark}>Track progress, updates, and key activities for your project</Typography>
                                </Box>
                                <Box mt={2} display="flex" gap={1}>
                                    <CommonButton
                                        label={ExecutionType.Details}
                                        width="140px"
                                        fontWeight={600}
                                        color={executionType === ExecutionType.Details ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={executionType === ExecutionType.Details ? theme.palette.primary.main : 'white'}
                                        handleClick={() => {
                                            setExecutionType(ExecutionType.Details);
                                            scrollToSection(detailsRef);
                                        }}
                                        marginBottom="0px"
                                    />
                                    <CommonButton
                                        label={ExecutionType.MileStone}
                                        width="180px"
                                        fontWeight={600}
                                        color={executionType === ExecutionType.MileStone ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={executionType === ExecutionType.MileStone ? theme.palette.primary.main : 'white'}
                                        handleClick={() => {
                                            setExecutionType(ExecutionType.MileStone);
                                            scrollToSection(milestoneRef);
                                        }}
                                        marginBottom="0px"
                                    />
                                    <CommonButton
                                        label={ExecutionType.Task}
                                        width="170px"
                                        fontWeight={600}
                                        color={executionType === ExecutionType.Task ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={executionType === ExecutionType.Task ? theme.palette.primary.main : 'white'}
                                        handleClick={() => {
                                            setExecutionType(ExecutionType.Task);
                                            scrollToSection(taskRef);
                                        }}
                                        marginBottom="0px"
                                    />
                                    <CommonButton
                                        label={ExecutionType.Reflection}
                                        width="190px"
                                        fontWeight={600}
                                        color={executionType === ExecutionType.Reflection ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={executionType === ExecutionType.Reflection ? theme.palette.primary.main : 'white'}
                                        handleClick={() => {
                                            setExecutionType(ExecutionType.Reflection);
                                            scrollToSection(reflectionRef);
                                        }}
                                        marginBottom="0px"
                                    />
                                </Box>
                            </Fragment>
                        }
                    />
                    <div ref={detailsRef}>
                        <Details fromMentor={fromMentor} projectData={projectData} getProjectDetails={getProjectDetails} />
                    </div>
                    <div ref={milestoneRef}>
                        <MileStone
                            fromMentor={fromMentor}
                            projectId={projectData?.project_id}
                            getProjectDetails={getProjectDetails}
                            progress={progress}
                            getMileStones={getMileStones}
                            mileStones={mileStones}
                        />
                    </div>
                    <div ref={taskRef}>
                        <WorkLogs
                            fromMentor={fromMentor}
                            projectData={projectData}
                            mileStones={mileStones}
                            studentId={searchParams.get('sid')}
                            studentData={studentData}
                            coachData={coachData}
                        />
                    </div>
                    <div ref={reflectionRef}>
                        <Reflection
                            fromMentor={fromMentor}
                            projectId={projectData?.project_id}
                        />
                    </div>
                </Grid>
                <Grid item md={4}>
                    <CurrentPlan
                        examType={ExamTypes.PROJECTMENTORING}
                        fromMentor={fromMentor}
                    />
                    <Events
                        fromMentor={fromMentor}
                        projectId={projectData?.project_id}
                        mentorId={projectData?.mentor_profile_id}
                        studentId={searchParams.get('sid')}
                        setInitialData={setInitialData}
                    />
                    <SessionHistory
                        fromMentor={fromMentor}
                        projectId={projectData?.project_id}
                        studentId={searchParams.get('sid')}
                    />
                    <Tasks
                        currentService={ServiceOffer.mentor}
                        subService={projectData?.project_id}
                        studentData={studentData}
                        studentId={searchParams.get('sid')}
                        fromMentor={fromMentor}
                    />
                    <ProjectSetting fromMentor={fromMentor} projectData={projectData} getProjectDetails={getProjectDetails} />
                    <ProjectMemberList fromMentor={fromMentor} projectId={projectData?.project_id} studentData={studentData} />
                    {!fromMentor && user?.user_type_id === UserTypeId.Student && (projectData?.student_id === user?.student_id) && (
                        <CommonCard
                            borderRadius={2}
                            paddingBottom="0px"
                            content={
                                <Box display="flex" justifyContent="space-between">
                                    <Box>
                                        <Typography fontWeight={600}>Delete Project</Typography>
                                        <Typography fontSize={13} color="background.dark">Once deleted, this project can not be recovered</Typography>
                                    </Box>
                                    <CommonButton
                                        label="Delete Project"
                                        color="White"
                                        width="33%"
                                        height="35px"
                                        background="red"
                                        marginBottom="0px"
                                        fontWeight={600}
                                        handleClick={() => setDeleteOpen(true)}
                                    />
                                </Box>
                            }
                        />
                    )}
                </Grid>
            </Grid>
        </Fragment>
    )
}

export default ProjectDetails